import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
	catchError,
	combineLatest,
	filter,
	map,
	of,
	switchMap,
	tap,
	withLatestFrom,
} from 'rxjs';

import { NgCommService } from '../../ajs-ng-communication.service';
import { AjsStateService } from '../../ajs-state.service';
import { states } from '../../core/application.states';
import { ChartRequester } from '../../requesters/chart/chart.requester';
import { ChartAdapter } from '../chart.adapter';
import {
	ChartAction,
	ChartActionPopupComponent,
	IChartActionModalData,
} from '../components/chart-action-popup/chart-action-popup.component';
import {
	bulkDeleteCharts,
	deleteChart,
	deleteChartFail,
	deleteChartSuccess,
	duplicateChart,
	duplicateChartFail,
	duplicateChartSuccess,
	fetchChartListFail,
	fetchChartListSuccess,
	initChartList,
	navigateToChart,
	navigateToChartCreate,
	openDeleteConfirmationPopup,
	refreshChartList,
	toggleChartStatus,
	updateIncludePublic,
} from './chart.actions';
import { ChartSelector } from './chart.selector';

@Injectable()
export class ChartEffects {
	// ##########################################################
	// Navigation Effects
	// ##########################################################
	public navigateToChartCreate = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToChartCreate),
				tap(() => this.stateService.go(states.ChartSelect))
			),
		{
			dispatch: false,
		}
	);

	public navigateToChart = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToChart),
				tap((a) => {
					const type = this.chartAdapter.getHdGraphTypeModel(
						a.chartType,
						a.chartSubType
					);
					return a.goToVisuTab
						? this.stateService.go(states.ChartVisualization, {
								id: `${a.id}`,
								type: `${type}`,
								mode: 'visualisation',
						  })
						: this.stateService.go(states.ChartEdit, {
								id: `${a.id}`,
								type: `${type}`,
						  });
				})
			),
		{
			dispatch: false,
		}
	);

	// ##########################################################
	// Business Effects
	// ##########################################################

	public initChartList$ = createEffect(() =>
		this.action$.pipe(
			ofType(initChartList),
			withLatestFrom(this.chartSelector.getIncludePublic$()),
			switchMap(([, includePublic]) =>
				this.chartRequester.getChartList(includePublic)
			),
			map((response) =>
				fetchChartListSuccess({
					chartList: this.chartAdapter.generateChartList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchChartListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public refreshChartList$ = createEffect(() =>
		this.action$.pipe(
			ofType(refreshChartList),
			withLatestFrom(this.chartSelector.getIncludePublic$()),
			switchMap(([, includePublic]) =>
				this.chartRequester.getChartList(includePublic, false)
			),
			map((response) =>
				fetchChartListSuccess({
					chartList: this.chartAdapter.generateChartList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchChartListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public updateIncludePublic$ = createEffect(() =>
		this.action$.pipe(
			ofType(updateIncludePublic),
			map(() => refreshChartList())
		)
	);

	public toggleActivationStatus$ = createEffect(() =>
		this.action$.pipe(
			ofType(toggleChartStatus),
			switchMap((action) =>
				combineLatest([
					of(action),
					this.chartRequester.getChart(
						action.chartId,
						action.chartType,
						action.chartSubType
					),
				])
			),
			switchMap(([action, chart]) =>
				this.chartRequester.changeActivationStatusChart(
					{
						...chart,
						metadata: {
							...chart.metadata,
							actif: !chart.metadata.actif,
						},
					},
					action.chartType,
					action.chartSubType
				)
			),
			map(() => refreshChartList()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(refreshChartList());
				return caught;
			})
		)
	);

	public deleteChart$ = createEffect(() =>
		this.action$.pipe(
			ofType(deleteChart),
			switchMap((action) =>
				this.chartRequester.deleteChart(
					action.chartId,
					action.chartType,
					action.chartSubType
				)
			),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@chart.delete.success:`
				);
				this.store.dispatch(deleteChartSuccess());
				return refreshChartList();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@chart.delete.error:`
				);
				this.store.dispatch(
					deleteChartFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public bulkDeleteCharts$ = createEffect(() =>
		this.action$.pipe(
			ofType(bulkDeleteCharts),
			switchMap((action) =>
				this.chartRequester.deleteManyCharts(action.chartIds)
			),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@chart.delete.multiple.success:`
				);
				this.store.dispatch(deleteChartSuccess());
				return refreshChartList();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@chart.delete.multiple.error:`
				);
				this.store.dispatch(
					deleteChartFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public duplicateChart$ = createEffect(() =>
		this.action$.pipe(
			ofType(duplicateChart),
			switchMap((action) =>
				combineLatest([
					of(action),
					this.chartRequester.duplicateChart(
						action.chartId,
						action.chartType,
						action.chartSubType
					),
				])
			),
			map(([action, id]) => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@chart.duplicate.success:`
				);
				return duplicateChartSuccess({
					id,
					chartType: action.chartType,
					chartSubType: action.chartSubType,
				});
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@chart.duplicate.error:`
				);
				this.store.dispatch(duplicateChartFail(error));
				return caught;
			})
		)
	);

	public navigateToChartEditAfterDuplicate$ = createEffect(() =>
		this.action$.pipe(
			ofType(duplicateChartSuccess),
			map((action) =>
				navigateToChart({
					id: action.id,
					chartType: action.chartType,
					chartSubType: action.chartSubType,
					goToVisuTab: false,
				})
			)
		)
	);

	// ##########################################################
	// Confirmation popup effects
	// ##########################################################
	public openDeleteConfirmationPopup$ = createEffect(() =>
		this.action$.pipe(
			ofType(openDeleteConfirmationPopup),
			switchMap((action) => {
				const modalRef = this.modalService.openPopup<IChartActionModalData>(
					ChartActionPopupComponent,
					{
						title: $localize`:i18n=@@chart.action.popup.delete.header:`,
						subTitle: '',
						chartId: action.chartId,
						element: action.chartLabel,
						action: ChartAction.Delete,
					},
					{
						disableClose: true,
					}
				);
				return combineLatest([of(action), modalRef.afterClosed()]);
			}),
			filter(([, modalResult]) => modalResult !== undefined),
			map(([action]) => {
				if (action.chartId.length === 1) {
					return deleteChart({
						chartId: action.chartId[0],
						chartType: action.chartType[0],
						chartSubType: action.chartSubType
							? action.chartSubType[0]
							: undefined,
					});
				} else {
					return bulkDeleteCharts({
						chartIds: action.chartId,
					});
				}
			})
		)
	);

	public constructor(
		private readonly action$: Actions,
		private readonly chartRequester: ChartRequester,
		private readonly chartAdapter: ChartAdapter,
		private readonly chartSelector: ChartSelector,
		private readonly store: Store,
		private readonly stateService: AjsStateService,
		private readonly modalService: AppModalService,
		private readonly ngCommService: NgCommService
	) {}
}
